import React from 'react'
import {StackScreenProps} from '@react-navigation/stack'

import {AppEvents, ManageActiveLoanEvents} from 'src/lib/Analytics/app_events'
import {BaseTemplate} from 'src/products/general/components/templates/BaseTemplate/BaseTemplate'
import {LoanPayNavigatorStack} from 'src/products/loans/MakePayment/LoanPayNavigatorStack'
import {MakePaymentSuccessTemplate} from 'src/products/loans/MakePayment/LoanPaySuccess/MakePaymentSuccessTemplate'
import {MainStackParamList} from 'src/nav/MainStackParamsList'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {trackLoansMakePaymentScreenRenderFailure} from 'src/products/loans/MakePayment/LoanPay.utils'
import {usePageViewedAnalytics} from 'src/lib/Analytics/usePageViewedAnalytics'

type LoanPaySuccessProps = Pick<
  StackScreenProps<LoanPayNavigatorStack, 'LoanPaySuccess'>,
  'route'
> &
  Pick<StackScreenProps<MainStackParamList>, 'navigation'>

/**
 * Displays a success message after a make loan payment has been submitted.
 * The payment amount and date displayed are passed as route params.
 */
const LoanPaySuccess = ({navigation, route}: LoanPaySuccessProps): JSX.Element => {
  usePageViewedAnalytics({
    eventName: ManageActiveLoanEvents.make_payment_success_screen_viewed,
    eventCategory: AppEvents.Category.ManageActiveLoan,
    eventArgs: {
      payment_method_type: route.params.paymentMethodTypeForAnalytics,
    },
  })

  const handleOnErrorBoundary = (e: Error): void => {
    trackLoansMakePaymentScreenRenderFailure(e, 'LoanPaySuccess')
  }

  const handleOnOkay = (): void => {
    TrackAppEvent(
      ManageActiveLoanEvents.make_payment_success_cta,
      AppEvents.Category.ManageActiveLoan,
    )
    navigation.reset({
      routes: [
        {
          name: 'Dashboard',
        },
      ],
    })
  }

  return (
    <BaseTemplate onErrorBoundary={handleOnErrorBoundary}>
      <MakePaymentSuccessTemplate
        onOkay={handleOnOkay}
        paymentAmount={route.params.paymentAmount}
        executeDate={route.params.initiationDate ?? ''}
      />
    </BaseTemplate>
  )
}

export {LoanPaySuccess}
